import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, Clock } from "lucide-react";

// Inactivity thresholds (product decision for a security platform)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;   // 15 minutes → hard logout
const WARN_BEFORE_MS  = 60 * 1000;        // 60 s warning modal
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll",
                         "touchstart", "wheel", "visibilitychange"];

/**
 * InactivityWatcher — closes the session automatically after
 * IDLE_TIMEOUT_MS of no user interaction. Shows a warning modal
 * WARN_BEFORE_MS before the actual logout so an active user can
 * stay signed in with a single click.
 *
 * Rendered inside <AuthProvider> so it always has access to `logout`.
 * Does nothing until the user is authenticated.
 */
export default function InactivityWatcher() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIvRef = useRef(null);

  useEffect(() => {
    if (!user) return; // Nothing to watch until authenticated

    const clearAll = () => {
      if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
      if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
      if (countdownIvRef.current) { clearInterval(countdownIvRef.current); countdownIvRef.current = null; }
    };

    const scheduleLogout = () => {
      clearAll();
      warnTimerRef.current = setTimeout(() => {
        setWarning(true);
        setCountdown(Math.round(WARN_BEFORE_MS / 1000));
        countdownIvRef.current = setInterval(() => {
          setCountdown((c) => Math.max(0, c - 1));
        }, 1000);
      }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
      logoutTimerRef.current = setTimeout(() => {
        clearAll();
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const onActivity = () => {
      // If the warning is showing, real activity does NOT auto-dismiss —
      // the user must click the button to reset intentionally.
      if (warning) return;
      lastActivityRef.current = Date.now();
      scheduleLogout();
    };

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true }));
    scheduleLogout();

    return () => {
      clearAll();
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, warning]);

  const stayActive = () => {
    setWarning(false);
    setCountdown(0);
    lastActivityRef.current = Date.now();
    // The effect will re-arm the timers on the next mount cycle (warning=false
    // triggers the effect via its dep, which recreates fresh timers).
  };

  const logoutNow = () => {
    setWarning(false);
    logout();
  };

  if (!user || !warning) return null;

  const mins = Math.floor(countdown / 60);
  const secs = countdown % 60;
  return (
    <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="inactivity-warning-modal">
      <div className="w-full max-w-md bg-card border border-sev-medium/60 p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 border border-sev-medium/60 bg-sev-medium/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-sev-medium" />
          </div>
          <div>
            <div className="overline text-sev-medium">Sesión a punto de expirar</div>
            <h2 className="font-display text-xl font-medium mt-1">
              ¿Sigues ahí?
            </h2>
            <p className="text-xs text-muted-foreground mt-2">
              Por seguridad cerramos tu sesión tras 15 minutos sin actividad
              (teclado o ratón). Vas a ser desconectado en:
            </p>
          </div>
        </div>

        <div className="border border-sev-medium/40 bg-sev-medium/5 p-4 text-center font-mono">
          <div className="flex items-center justify-center gap-2 text-sev-medium">
            <Clock className="w-4 h-4" />
            <span className="text-3xl font-display font-medium"
                  data-testid="inactivity-countdown">
              {mins}:{secs.toString().padStart(2, "0")}
            </span>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={logoutNow}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
                  data-testid="inactivity-logout-btn">
            Cerrar sesión ahora
          </button>
          <button onClick={stayActive}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 inline-flex items-center gap-2"
                  data-testid="inactivity-stay-btn">
            Sigo aquí
          </button>
        </div>
      </div>
    </div>
  );
}
